/**
 * Copyright IBM Corp. 2022, 2026
 */

import React, { Fragment } from 'react'
import strftime from 'strftime'
import slugify from 'slugify'
import { render as toPlainText } from 'datocms-structured-text-to-plain-text'
import { isHeading } from 'datocms-structured-text-utils'
import { renderNodeRule, StructuredText } from 'react-datocms/structured-text'
import SocialEmbed from '../../../../_components/social-embed'
import CodeBlockBlog from './code-block-blog'
import BlogVideo from './blog-video'
import BlogImage from './blog-image'
import RelatedContent from './related-content'
import BlogCtaButton from './blog-cta-button'
import Table from 'components/materia/sub-blocks/structured-text/table'
import sendPosthogEvent from '@web/utils/send-posthog-event'
import s from './style.module.css'

export default function BlogPostContent({ className, content }) {
	const handleLinkClick = (event) => {
		const target = event.target
		if (!(target instanceof Element)) return
		const link = target.closest('a')
		if (!link) return
		sendPosthogEvent('Blog Body Link Clicked', {
			href: link.getAttribute('href') ?? '',
			text: link.textContent?.trim() ?? '',
		})
	}
	return (
		<div className={className} onClick={handleLinkClick}>
			<StructuredText
				data={content}
				customNodeRules={[
					// Add HTML anchors to heading levels for in-page navigation
					renderNodeRule(isHeading, ({ node, children, key }) => {
						const HeadingTag = `h${node.level}`
						const anchor = slugify(toPlainText(node), { lower: true })

						return (
							<HeadingTag key={key}>
								<a
									className="__permalink-h"
									href={`#${anchor}`}
									aria-label={`${toPlainText(node).toLowerCase()} permalink`}
								>
									»
								</a>
								<a className="__target-h" id={anchor} aria-hidden="true" />
								{children}
							</HeadingTag>
						)
					}),
				]}
				renderBlock={({ record }, index) => {
					switch (record.__typename) {
						case 'StTableRecord':
							return (
								<Table
									key={record.id}
									table={record.table}
									expand={record.expand}
								/>
							)
						case 'StCodeBlockRecord':
							return (
								<CodeBlockBlog
									key={record.id}
									code={record.code}
									language={record.language.slug}
									showClipboard={record.showClipboard}
									filename={record.filename}
								/>
							)
						case 'BlogVideoRecord':
							return (
								<BlogVideo
									key={record.id}
									url={record.url}
									description={record.description}
								/>
							)
						case 'DatoVideoRecord':
							return (
								<BlogVideo
									key={record.id}
									url={record.video.url}
									description={record.description}
								/>
							)
						case 'BlogImageRecord': {
							const nextBlockType = content[index + 1]?.type

							return (
								<Fragment key={record.id}>
									<BlogImage
										image={record.image}
										enableLightbox={record.enableLightbox}
										caption={record.caption}
										imagePosition={record.imagePosition}
										size={record.size}
										priority={index === 0}
									/>
									{/* Clears the float for left/right-positioned images
                  if the next block is not text or markdown,
                  or if there are no more blocks
                */}
									{(record.size !== 'center' &&
										!['text', 'legacy'].includes(nextBlockType)) ||
									record.index === content.length - 1 ? (
										<div className={s.clearfix} />
									) : null}
								</Fragment>
							)
						}
						case 'SocialEmbedRecord':
							return <SocialEmbed key={record.url} url={record.url} />
						// TODO Remove this case once the Tweet Embed block is removed
						case 'TweetEmbedRecord': {
							return (
								<SocialEmbed
									key={record.tweetId}
									url={`https://twitter.com/user/status/${record.tweetId}`}
								/>
							)
						}
						case 'BlogInlineRelatedArticleRecord':
							return (
								<RelatedContent
									key={record.id}
									eyebrow="Related Content"
									title={record.title}
									summary={`${strftime.timezone(0)(
										'%b %d %Y',
										new Date(record.postDate),
									)} | ${record.publication}`}
									link={{
										url: record.articleUrl,
										type: 'outbound',
									}}
								/>
							)
						case 'BlogCtaButtonRecord':
							return (
								<BlogCtaButton
									key={record.id}
									text={record.buttonText}
									url={record.buttonUrl}
								/>
							)
						default:
							return null
					}
				}}
			/>
		</div>
	)
}
